import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SafeHTML from '../components/SafeHTML';
import { getFaqKey } from '../utils/keys';

const AustraliaVisaPage = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: t('auFaq1Q'),
      answer: t('auFaq1A')
    },
    {
      question: t('auFaq2Q'),
      answer: t('auFaq2A')
    },
    {
      question: t('auFaq3Q'),
      answer: t('auFaq3A')
    },
    {
      question: t('auFaq4Q'),
      answer: t('auFaq4A')
    },
    {
      question: t('auFaq5Q'),
      answer: t('auFaq5A')
    },
    {
      question: t('auFaq6Q'),
      answer: t('auFaq6A')
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section 
        className="relative h-[500px] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=1920&q=80)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            <div className="inline-block bg-yellow-400 text-black px-4 py-2 rounded font-bold mb-4">
              {t('auBadge')}
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('auHeroTitle')}
            </h1>
            <p className="text-xl text-white/90 mb-8">
              {t('auHeroSubtitle')}
            </p>
            <a
              href="https://wa.me/971585862103?text=Hello%2C%20I%20would%20like%20to%20apply%20for%20an%20Australia%20Visa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
            >
              {t('auHeroBtn')}
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        <div className="prose prose-lg max-w-none mb-16">
          <SafeHTML tag="p" className="text-gray-700 text-lg leading-relaxed" html={t('auIntro1')} />
          <SafeHTML tag="p" className="text-gray-700 text-lg leading-relaxed mt-4" html={t('auIntro2')} />
        </div>

        <div className="mb-16 bg-blue-50 p-8 rounded-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('auHowToGetTitle')}</h2>
          <SafeHTML tag="p" className="text-gray-700 mb-4" html={t('auHowToGetDesc')} />
          <ul className="space-y-2 text-gray-700">
            <li><SafeHTML tag="span" html={'• ' + t('auHowToGet1')} /></li>
            <li><SafeHTML tag="span" html={'• ' + t('auHowToGet2')} /></li>
            <li><SafeHTML tag="span" html={'• ' + t('auHowToGet3')} /></li>
          </ul>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('auVisaTypesTitle')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('auVisaType1Name')}</h3>
              <p className="text-gray-700">{t('auVisaType1Desc')}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('auVisaType2Name')}</h3>
              <p className="text-gray-700">{t('auVisaType2Desc')}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('auVisaType3Name')}</h3>
              <p className="text-gray-700">{t('auVisaType3Desc')}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('auVisaType4Name')}</h3>
              <p className="text-gray-700">{t('auVisaType4Desc')}</p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('auWhyApplyTitle')}</h2>
          <div className="space-y-6">
            <div className="p-6 bg-blue-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('auWhyApply1Title')}</h3>
              <SafeHTML tag="p" className="text-gray-700" html={t('auWhyApply1Desc')} />
            </div>
            <div className="p-6 bg-blue-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('auWhyApply2Title')}</h3>
              <SafeHTML tag="p" className="text-gray-700" html={t('auWhyApply2Desc')} />
            </div>
            <div className="p-6 bg-blue-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('auWhyApply3Title')}</h3>
              <p className="text-gray-700">{t('auWhyApply3Desc')}</p>
            </div>
            <div className="p-6 bg-blue-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('auWhyApply4Title')}</h3>
              <p className="text-gray-700">{t('auWhyApply4Desc')}</p>
            </div>
            <div className="p-6 bg-blue-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('auWhyApply5Title')}</h3>
              <SafeHTML tag="p" className="text-gray-700" html={t('auWhyApply5Desc')} />
            </div>
          </div>
        </div>

        <div className="mb-16 bg-gradient-to-r from-logo-blue to-logo-cyan p-8 rounded-lg text-white">
          <h2 className="text-3xl font-bold mb-4">{t('auNzPromoTitle')}</h2>
          <p className="text-lg mb-6">
            {t('auNzPromoDesc')}
          </p>
          <a
            href="https://wa.me/971585862103?text=I%20want%20Australia%20and%20New%20Zealand%20visas"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-logo-blue px-8 py-3 rounded-lg font-semibold hover:shadow-xl transition-all"
          >
            {t('auNzPromoBtn')}
          </a>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('auRequirementsTitle')}</h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('auReq1')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('auReq2')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('auReq3')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('auReq4')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('auReq5')}</span>
            </li>
          </ul>
          <SafeHTML tag="p" className="text-gray-600 mt-4 text-sm" html={t('auProcessingTime')} />
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">{t('auFaqTitle')}</h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={getFaqKey(faq.question, index)} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-4 bg-gray-50">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
};

export default AustraliaVisaPage;
